/**
 * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */
import { Plugin } from 'ckeditor5/src/core.js';
import { Collection, uid } from 'ckeditor5/src/utils.js';
import { escapeRegExp } from 'lodash-es';
import { CkeditorConstants } from '../../../src/utils';

const { CUSTOM_ELEMENT_TO_IGNORE } = CkeditorConstants;
/**
 * A set of helpers related to find and replace.
 */
export default class FindAndReplaceUtils extends Plugin {
    /**
     * @inheritDoc
     */
    static get pluginName() {
        return 'FindAndReplaceUtils';
    }
    /**
     * Executes findCallback and updates search results list.
     *
     * @param range The model range to scan for matches.
     * @param model The model.
     * @param findCallback The callback that should return `true` if provided text matches the search term.
     * @param startResults An optional collection of find matches that the function should
     * start with. This would be a collection returned by a previous `updateFindResultFromRange()` call.
     * @returns A collection of objects describing find match.
     *
     * An example structure:
     *
     * ```js
     * {
     *	id: resultId,
     *	label: foundItem.label,
     *	marker
     *	}
     * ```
     */
    updateFindResultFromRange(range, model, findCallback, startResults) {
        const results = startResults || new Collection();
        const checkIfResultAlreadyOnList = (marker) => results.find(markerItem => {
            const { marker: resultsMarker } = markerItem;
            const resultRange = resultsMarker.getRange();
            const markerRange = marker.getRange();
            return resultRange.isEqual(markerRange);
        });
        model.change(writer => {
            [...range].forEach(({ type, item }) => {
                if (type === 'elementStart') {
					try {
                    if (model.schema.checkChild(item, '$text')) {

                        const rangeOnItem = writer.createRangeIn(item);
                        // Check if range contains any custom inline widgets
                        const containsInlineWidget = [...rangeOnItem.getItems()].some(node =>
                            CUSTOM_ELEMENT_TO_IGNORE.includes(node.name)
                        );
                        const textContent = containsInlineWidget
                            ? this.rangeToTextWithoutInlineData(rangeOnItem)
                            : this.rangeToText(model.createRangeIn(item));

                        let foundItems = findCallback({ item, text: textContent });
                        if (!foundItems) {
                            return;
                        }
                        Array.isArray(foundItems) ? null : foundItems = foundItems.results;
                        foundItems.forEach(foundItem => {
                            const resultId = `findResult:${uid()}`;
                            const marker = writer.addMarker(resultId, {
                                usingOperation: false,
                                affectsData: false,
                                range: writer.createRange(writer.createPositionAt(item, foundItem.start), writer.createPositionAt(item, foundItem.end))
                            });
                            const index = findInsertIndex(results, marker);
                            if (!checkIfResultAlreadyOnList(marker)) {
                                results.add({
                                    id: resultId,
                                    label: foundItem.label,
                                    marker
                                }, index);
                            }
                        });
                    }
					} catch (error) {
						// Required for debugging purposes
						console.warn(item, error);
					}
                }
            });
        });
        return results;
    }
    /**
     * Returns text representation of a range. The returned text length should be the same as range length.
     * In order to achieve this, this function will replace inline elements (text-line) as new line character ("\n").
     *
     * @param range The model range.
     * @returns The text content of the provided range.
     */
    rangeToText(range) {
        return Array.from(range.getItems()).reduce((rangeText, node) => {
            // Trim text to a last occurrence of an inline element and update range start.
            if (!(node.is('$text') || node.is('$textProxy'))) {
                // Editor has only one inline element defined in schema: `<softBreak>` which is treated as new line character in blocks.
                // Special handling might be needed for other inline elements (inline widgets).
                return `${rangeText}\n`;
            }
            return rangeText + node.data;
        }, '');
    }
	/**
	 * Returns text representation of a range. The returned text length should be the same as range length.
	 * In order to achieve this, this function will replace inline elements (text-line) as single space character (" ").
	 * And textdata/textcontent inside inline element will replace as empty string character ("").
	 *
	 * 
	 * @param {module:engine/model/range~Range} range The model range.
	 * @returns {String} The text content of the provided range.
	 */
	rangeToTextWithoutInlineData(range) {
		return [...range.getWalker({ignoreElementEnd : true})].reduce((rangeText,node) => {

			if(CUSTOM_ELEMENT_TO_IGNORE.includes(node.item?.parent?.name)) {
				return rangeText;
			}

			if (CUSTOM_ELEMENT_TO_IGNORE.includes(node.item?.name) && !(node.item.is('text') || node.item.is('textProxy'))) {
				return rangeText + ' ';
			}
			return rangeText + (node.item.data || "");
		},'');
	}
    /**
     * Creates a text matching callback for a specified search term and matching options.
     *
     * @param searchTerm The search term.
     * @param options Matching options.
     * 	- options.matchCase=false If set to `true` letter casing will be ignored.
     * 	- options.wholeWords=false If set to `true` only whole words that match `callbackOrText` will be matched.
     */
    findByTextCallback(searchTerm, options) {
        let flags = 'gu';
        if (!options.matchCase) {
            flags += 'i';
        }
        let regExpQuery = `(${escapeRegExp(searchTerm)})`;
        if (options.wholeWords) {
            const nonLetterGroup = '[^a-zA-Z\u00C0-\u024F\u1E00-\u1EFF]';
            if (!new RegExp('^' + nonLetterGroup).test(searchTerm)) {
                regExpQuery = `(^|${nonLetterGroup}|_)${regExpQuery}`;
            }
            if (!new RegExp(nonLetterGroup + '$').test(searchTerm)) {
                regExpQuery = `${regExpQuery}(?=_|${nonLetterGroup}|$)`;
            }
        }
        const regExp = new RegExp(regExpQuery, flags);
        function findCallback({ text }) {
            const matches = [...text.matchAll(regExp)];
            return matches.map(regexpMatchToFindResult);
        }
        return findCallback;
    }
}
// Finds the appropriate index in the resultsList Collection.
function findInsertIndex(resultsList, markerToInsert) {
    const result = resultsList.find(({ marker }) => {
        return markerToInsert.getStart().isBefore(marker.getStart());
    });
    return result ? resultsList.getIndex(result) : resultsList.length;
}
/**
 *  Maps RegExp match result to find result.
 */
function regexpMatchToFindResult(matchResult) {
    const lastGroupIndex = matchResult.length - 1;
    // Determine the start index of the matched word:
    // - If the input string starts with a space(eg. answers of quiz questions), calculate the index manually using matchResult.input.indexOf(matchResult[0]) - 1
    // - Otherwise, use the default start index provided by matchResult.index
    // Example 1 :
    // searched text = 'Petroleum'
    // structure of target quiz question :
    //    What should you apply  to a nasalor face mask to ensure a proper seal?
    //    ✔️ Water-soluble lubricant
    //    ❌ Petroleum jelly
    //    ❌ Alcohol
    //    ❌ Adhesive cream
    // matchResult = ['Petroleum', 'Petroleum', index: 2, input: '  Petroleum jelly', groups: undefined]
    // In this particular case ckeditor find and replace gives index as 2, so we need to set start index of
    // our find result as correct index of searched text in the input
    let startOffset = matchResult.input.startsWith(' ') ? matchResult.input.indexOf(matchResult[0]) - 1 : matchResult.index;
    // Searches with match all flag have an extra matching group with empty string or white space matched before the word.
    // If the search term starts with the space already, there is no extra group even with match all flag on.
    if (matchResult.length === 3) {
        startOffset += matchResult[1].length;
    }
    return {
        label: matchResult[lastGroupIndex],
        start: startOffset,
        end: startOffset + matchResult[lastGroupIndex].length
    };
}
